/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ApplicationTablesComponent Component
 *----------------------------
 * Uses Bootstrap .table and .table-responsive for a sleek, boxed, and responsive table list.
 * Features:
 * - Borderless header, perfectly aligned with the boxed table
 * - Responsive and scrollable
 * - All column headers/values right-aligned except Table Name
 * - Padding between fields and actions
 * - Action icons with tooltips
 * - Mobile-friendly, clean look
 */

import React, { useState, useEffect } from "react";
import { useTheme } from "../../theme/ThemeContext";
import Paginator from "../common/Paginator";
import APIHelper from "../../api/APIHelper";
import envConfig from "../../config/envConfig";
import { useNavigate } from 'react-router-dom';
import { retrieve } from "../../utils/storageHelper";
import { useAlertDialog } from '../common/AlertDialogContext';
import ProgressBarDialog from '../common/ProgressBar';
import TableDetailsModal from "./ApplicationTableDetialsComponent";
import { useQuery } from '@tanstack/react-query';
import Button from "../Button";
import ColumnOrderModal from "./ColumnOrderModal";

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

export default function ApplicationTablesComponent({ applicationId }) {
    const navigate = useNavigate();
    const { theme } = useTheme();
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const perPage = 5;
    const [totalTables, setTotalTables] = useState(0);
    const [search, setSearch] = useState("");
    const [deleting, setDeleting] = useState(false);
    const { showAlertDialog } = useAlertDialog();
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedTableDetails, setSelectedTableDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [orderModalOpen, setOrderModalOpen] = useState(false);
    const [currentTableForOrder, setCurrentTableForOrder] = useState(null);

    const userData = retrieve("userData");

    const tablesQueryKey = ['applicationTables', applicationId, page, perPage, search];

    const {
        data: tablesQueryData,
        error: tablesQueryError,
        isLoading: tablesQueryLoading,
        isFetching: tablesQueryFetching,
    } = useQuery({
        queryKey: tablesQueryKey,
        queryFn: async ({ signal }) => {
            if (!applicationId) return { results: [], count: 0 };

            let url = `/arcee/application/table/list/?application_id=${applicationId}&page=${page}&page_size=${perPage}`;
            if (search && search.trim()) {
                url += `&search=${encodeURIComponent(search.trim())}`;
            }

            const resp = await apiHelper.apiCall(url, { method: 'GET', signal });
            if (!resp.ok) {
                let j = {};
                try { j = await resp.json(); } catch (e) { /* ignore */ }
                throw new Error(j?.error || `Failed to fetch tables (${resp.status})`);
            }
            return await resp.json();
        },
        enabled: !!applicationId,
        staleTime: 30_000,
        retry: 1,
    });

    useEffect(() => {
        setLoading(Boolean(tablesQueryLoading || tablesQueryFetching));

        if (tablesQueryError) {
            setError(tablesQueryError?.message || String(tablesQueryError));
            setTables([]);
            setTotalTables(0);
            return;
        }

        if (tablesQueryData) {
            setTables(tablesQueryData.results || []);
            setTotalTables(tablesQueryData.count || 0);
            setError(null);
        }
    }, [tablesQueryData, tablesQueryError, tablesQueryLoading, tablesQueryFetching]);

    const pageCount = Math.max(1, Math.ceil(totalTables / perPage));
    const handlePageChange = (newPage) => setPage(newPage);
    const handlePrevious = () => setPage(p => Math.max(p - 1, 1));
    const handleNext = () => setPage(p => Math.min(p + 1, pageCount));

    const handleCreateClick = () => {
        navigate(`/dashboard/applications/${applicationId}/table_create`);
    };

    async function handleRowAction(action, table) {
        if (action === 'delete') {
            showAlertDialog({
                title: "Confirm Deletion",
                message: "Are you sure you want to delete this table?",
                type: "info",
                okLabel: "Yes",
                cancelLabel: "Cancel",
                showCancel: true,
                onOk: async () => {
                    setDeleting(true);
                    try {
                        const userData = retrieve('userData');
                        if (!userData) {
                            showAlertDialog({
                                title: "Error",
                                message: "Session may have expired. Please login again.",
                                type: "error",
                                okLabel: "Close",
                                showCancel: false,
                                onOk: () => window.location.reload(),
                            });
                            return;
                        }
                        const payload = {
                            table_name: table.name,
                            application_id: applicationId,
                        };
                        const response = await apiHelper.apiCall('/arcee/application/table/delete/', {
                            method: 'DELETE',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(payload),
                        });
                        if (!response.ok) {
                            const errData = await response.json();
                            showAlertDialog({
                                title: "Error",
                                message: errData.message || response.statusText,
                                type: "error",
                                okLabel: "Close",
                                showCancel: false,
                                onOk: () => window.location.reload(),
                            });
                            return;
                        }
                        showAlertDialog({
                            title: "Success",
                            message: "Table deleted successfully!",
                            type: "success",
                            okLabel: "Close",
                            showCancel: false,
                            onOk: () => window.location.reload(),
                        });
                    } catch (error) {
                        console.error('Delete error:', error);
                        showAlertDialog({
                            title: "Error",
                            message: "An error occurred during deletion. Please try again later.",
                            type: "error",
                            okLabel: "Close",
                            showCancel: false,
                            onOk: () => window.location.reload(),
                        });
                    } finally {
                        setDeleting(false);
                    }
                },
                onCancel: () => { },
            });
        } if (action === 'view') {
            fetchTableDetails(table.name);
        }
        if (action === 'edit') {
            navigate(
                `/dashboard/applications/${applicationId}/${table.name}/edit`
            );
            return;
        }
        if (action === 'configure') {
            fetchTableDetails(table.name, true);
        }
    }

    async function fetchTableDetails(tableName, forOrdering = false) {
        setLoadingDetails(true);
        try {
            const payload = {
                application_id: applicationId,
                table_name: tableName
            };
            const resp = await apiHelper.apiCall('/arcee/application/table/details/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!resp.ok) throw new Error("Failed to fetch details");
            const data = await resp.json();
            if (forOrdering) {
                setCurrentTableForOrder(data);
                setOrderModalOpen(true);
            } else {
                setSelectedTableDetails(data);
                setDetailsModalOpen(true);
            }
        } catch (err) {
            console.error(err);
            alert("Error fetching table details");
        } finally {
            setLoadingDetails(false);
        }
    }

    return (
        <div className="container" style={{ margin: "36px auto", fontFamily: theme.fontFamily }}>
            <div className="row align-items-center mb-2 gx-2">
                <div className="col">
                    <input
                        type="text"
                        value={search}
                        onChange={e => {
                            setSearch(e.target.value.slice(0, 128));
                            setPage(1);
                        }}
                        placeholder="Search tables by display name or name"
                        className="form-control"
                        style={{
                            fontSize: 16,
                            border: `1.1px solid ${theme.primary}`,
                            borderRadius: theme.buttonRadius,
                            background: "#fafbfc"
                        }}
                        maxLength={128}
                    />
                </div>
                <div className="col-auto">
                    <Button
                        mode="primary-bold"
                        onClick={handleCreateClick}
                    >
                        + Create
                    </Button>
                </div>
            </div>

            {/* Desktop View (> 991px) */}
            <div
                className="table-responsive rounded shadow-sm d-none d-lg-block"
                style={{
                    border: `1.2px solid ${theme.primary}`,
                    background: "#fff",
                    overflowX: "auto",
                    padding: 0
                }}
            >
                <table className="table mb-0 align-middle" style={{ minWidth: 820 }}>
                    <thead>
                        <tr style={{
                            fontWeight: 600, color: theme.primary, background: "#f7f7fa", fontSize: 16
                        }}>
                            <th style={{ minWidth: 140, textAlign: "left" }}>Table Name</th>
                            <th className="text-end" style={{ minWidth: 160 }}>Display Name</th>
                            <th className="text-end" style={{ minWidth: 220 }}>Description</th>
                            <th className="text-end" style={{ minWidth: 70 }}>Fields</th>
                            <th className="text-end" style={{ minWidth: 90 }}>Version</th>
                            <th className="text-end" style={{ minWidth: 110 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr style={{ height: 150 }}>
                                <td colSpan={6} style={{
                                    textAlign: "center", color: theme.primary, fontSize: 14, padding: "20px"
                                }}>
                                    <div className="d-flex flex-column align-items-center gap-2">
                                        <i className="bi bi-arrow-repeat animate-spin" style={{ fontSize: "24px" }}></i>
                                        Loading tables...
                                    </div>
                                </td>
                            </tr>
                        ) : error ? (
                            <tr style={{ height: 150 }}>
                                <td colSpan={6} style={{ textAlign: "center", color: "red", fontSize: 14, padding: "20px" }}>
                                    {error}
                                </td>
                            </tr>
                        ) : tables.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="text-center" style={{ color: "#aaa", fontStyle: "italic", padding: "28px 0" }}>
                                    No tables found.
                                </td>
                            </tr>
                        ) : (
                            tables.map(t => (
                                <tr key={t.name} style={{ fontSize: 16 }}>
                                    <td style={{ fontFamily: "monospace", color: "#435", textAlign: "left", verticalAlign: "middle" }}>{t.name}</td>
                                    <td className="text-end align-middle fw-medium">{t.display_name || '-'}</td>
                                    <td className="text-end align-middle" style={{ color: "#5e6380" }}>{t.description}</td>
                                    <td className="text-end align-middle" style={{ color: theme.primary, fontWeight: 600 }}>{t.fields_count}</td>
                                    <td className="text-end align-middle" style={{ color: "#5e6380" }}>v{t.created_version_code}</td>
                                    <td className="text-end align-middle">
                                        <div className="d-flex gap-3">
                                            <i className="bi bi-eye" title="View" style={{ cursor: "pointer", color: "#6980ff", fontSize: '1.2rem' }} onClick={() => handleRowAction("view", t)} />
                                            <i className="bi bi-arrow-down-up" title="Configure Columns" style={{ cursor: "pointer", color: theme.primary, fontSize: '1.2rem' }} onClick={() => handleRowAction("configure", t)} />
                                            <i className="bi bi-pencil" title="Edit" style={{ cursor: "pointer", color: "#f1a100", fontSize: '1.2rem' }} onClick={() => handleRowAction("edit", t)} />
                                            <i className="bi bi-trash" title="Delete" style={{ cursor: "pointer", color: "#e14f4f", fontSize: '1.2rem' }} onClick={() => handleRowAction("delete", t)} />
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Mobile View (<= 991px) - Stacked Cards */}
            <div className="d-lg-none">
                {loading ? (
                    <div className="text-center py-5 text-primary">
                        <i className="bi bi-arrow-repeat animate-spin fs-1 mb-2"></i>
                        <div>Loading tables...</div>
                    </div>
                ) : error ? (
                    <div className="text-center py-5 text-danger">{error}</div>
                ) : tables.length === 0 ? (
                    <div className="text-center py-5 text-muted fst-italic">No tables found.</div>
                ) : (
                    <div className="d-flex flex-column gap-3">
                        {tables.map(t => (
                            <div key={t.name} className="p-3 bg-white rounded border shadow-sm" style={{ borderColor: theme.primary }}>
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div className="fw-bold fs-5 text-break" style={{ fontFamily: 'monospace', color: '#435' }}>
                                        {t.name}
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="badge bg-light text-primary border" style={{ fontSize: '12px' }}>
                                            {t.fields_count} Fields
                                        </span>
                                        <span className="badge bg-light text-muted border" style={{ fontSize: '12px' }}>
                                            v{t.created_version_code}
                                        </span>
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <div className="small text-muted text-uppercase fw-bold" style={{ fontSize: '11px' }}>Display Name</div>
                                    <div className="fw-medium">{t.display_name || '-'}</div>
                                </div>
                                {t.description && (
                                    <div className="mb-3">
                                        <div className="small text-muted text-uppercase fw-bold" style={{ fontSize: '11px' }}>Description</div>
                                        <div className="text-muted small text-break">{t.description}</div>
                                    </div>
                                )}
                                <div className="d-flex justify-content-end flex-wrap gap-2 mt-3 pt-3 border-top">
                                    <button className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2" onClick={() => handleRowAction("view", t)}>
                                        <i className="bi bi-eye"></i> View
                                    </button>
                                    <button className="btn btn-sm btn-outline-info d-flex align-items-center gap-2" onClick={() => handleRowAction("configure", t)}>
                                        <i className="bi bi-gear"></i> Configure
                                    </button>
                                    <button className="btn btn-sm btn-outline-warning d-flex align-items-center gap-2" onClick={() => handleRowAction("edit", t)}>
                                        <i className="bi bi-pencil"></i> Edit
                                    </button>
                                    <button className="btn btn-sm btn-outline-danger d-flex align-items-center gap-2" onClick={() => handleRowAction("delete", t)}>
                                        <i className="bi bi-trash"></i> Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {detailsModalOpen && (
                <TableDetailsModal
                    theme={theme}
                    table={selectedTableDetails}
                    onClose={() => setDetailsModalOpen(false)}
                />
            )}

            {orderModalOpen && (
                <ColumnOrderModal
                    theme={theme}
                    table={currentTableForOrder}
                    applicationId={applicationId}
                    apiHelper={apiHelper}
                    onClose={() => setOrderModalOpen(false)}
                    onSave={() => {
                        // Optionally refresh tables or just show success
                    }}
                />
            )}

            {loadingDetails && (
                <ProgressBarDialog open={loadingDetails} message="Loading table details..." cancellable={false} />
            )}



            {/* Show pagination only if no error */}
            {!error && (
                <Paginator
                    page={page}
                    pageCount={pageCount}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    onPage={handlePageChange}
                    maxPagesToShow={3}
                />
            )}


            <ProgressBarDialog
                open={deleting}
                text_message="Deleting table..."
                cancellable={false}
            />
        </div >
    );
}
